'use client';
import { cn } from '@/lib/utils';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';

import styles from './richText.module.scss';

export type RichTextProps = {
  content?: Parameters<typeof documentToReactComponents>[0];
  size?: string;
  weight?: string;
  colorHeading?: string;
  colorText?: string;
  colorLink?: string;
  textAlign?: string;
  listIcon?: string;
} & HTMLAttributes<HTMLHeadingElement>;

const richTextOptions: Options = {
  renderNode: {
    [BLOCKS.TABLE]: (_node, children: ReactNode) => (
      <div className={styles.tableWrapper}>
        <table>
          <tbody>{children}</tbody>
        </table>
      </div>
    ),

    [BLOCKS.TABLE_ROW]: (_node, children: ReactNode) => <tr>{children}</tr>,

    [BLOCKS.TABLE_CELL]: (_node, children: ReactNode) => <td>{children}</td>,

    [BLOCKS.TABLE_HEADER_CELL]: (_node, children: ReactNode) => <th>{children}</th>,

    [INLINES.HYPERLINK]: (node, children: ReactNode) => {
      const uri: string = node.data.uri ?? '';
      const isInternal = uri.startsWith('/') || uri.startsWith('#');
      return (
        <a href={uri} {...(!isInternal && { target: '_blank', rel: 'noopener noreferrer' })}>
          {children}
        </a>
      );
    },
  },
};

export const RichText = forwardRef<HTMLHeadingElement, RichTextProps>(
  (
    { content, size, weight, colorHeading, colorText, colorLink, textAlign, listIcon, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          size,
          weight,
          colorHeading,
          colorText,
          colorLink,
          textAlign,
          listIcon,
          styles.wrap,
        )}
        {...props}
      >
        {content ? documentToReactComponents(content, richTextOptions) : null}
      </div>
    );
  },
);

RichText.displayName = 'Rich text';
